// --- React & Redux
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
// --- External UI Libraries
import { Col, Row } from 'antd';
import Input from '@dtci/spellbook/Input';
import Button from '@dtci/spellbook/Button';
import Select from '@dtci/spellbook/Select';
// --- Hooks & Actions
import { useProductsActions } from 'datastage/store/products/useProducts';
import { useProductsData } from '../../hooks/useProductsData';
import { usePermissions } from 'datastage/utils/permissions';
// --- Components
import { InputLabel } from 'datastage/components/InputLabel';
// --- Utilities
import { getItemRange } from 'datastage/utils/pagination';
import { parseSearchParams, updateSearchParams } from 'datastage/utils/urlParams';
import { validateSpecialCharacters } from 'datastage/utils/validateSpecialCharacters';
import {
    cleanProductFilter,
    findClassByValue,
    findLegalStatusByValue,
    findLoadStatusValue,
} from './utils';
// --- Styles
import './styles.scss';

/**
 * Products Search Form
 *
 * Features:
 * - Renders a filter form for Products (Product ID, Title, FOX WPR ID, Class, Load Status, Legal Status, Start Year, End Year)
 * - Debounced auto-search on input change (400ms delay)
 * - Input validation for ID fields
 * - Syncs filter state with URL params bidirectionally
 * - Reset button clears all filters
 *
 * @returns {JSX.Element}
 */
export function ProductsSearchForm() {
    const navigate = useNavigate();
    const location = useLocation();
    const isInitialMount = useRef(true);
    const debounceTimerRef = useRef(null);

    const { fetchProducts, updateProducts, productRange, productSort, itemsPerPage } =
        useProductsData();
    const { updateProductsSearchForm, clearProducts } = useProductsActions();
    const { filterForm, classOptions, legalStatusOptions, loadStatusOptions } = useSelector(
        (s) => s.products,
    );
    const { isLegalUser } = usePermissions();

    // Legal Users have restricted Legal Status options (exclude Draft=2 and Processing=7)
    const filteredLegalStatusOptions = isLegalUser
        ? legalStatusOptions.filter((opt) => [1, 3, 4, 5].includes(Number(opt.value)))
        : legalStatusOptions;

    // Validation state for inline error messages
    const [validationErrors, setValidationErrors] = useState({
        productId: undefined,
        foxFinancialTitleId: undefined,
    });

    // Sync URL params to form state on navigation (location.key changes on each navigation)
    useEffect(() => {
        const params = parseSearchParams(location.search);
        const filterData = buildFilterFromParams(
            params,
            classOptions,
            legalStatusOptions,
            loadStatusOptions,
        );

        // Handle pageSize from URL (TAP pattern)
        let rangeOverride;
        if (params.pageSize) {
            const newPageSize = parseInt(params.pageSize, 10);
            if (!isNaN(newPageSize) && newPageSize > 0) {
                rangeOverride = getItemRange(0, newPageSize);
                updateProducts({
                    itemsPerPage: newPageSize,
                    productRange: rangeOverride,
                    currentPage: 0,
                });
            }
        }

        const hasUrlParams = Object.keys(filterData).length > 0;

        if (hasUrlParams) {
            updateProductsSearchForm({ key: 'filterForm', data: filterData });
        }

        // Merge URL-derived filters onto Redux filterForm so defaults apply when the URL
        // only carries partial params (e.g. pageSize).
        const filterForFetch = hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

        fetchData(filterForFetch, rangeOverride);
        isInitialMount.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key]);

    // Re-parse URL params when options load (needed for select values that depend on options)
    useEffect(() => {
        if (isInitialMount.current) return;
        if (!Array.isArray(classOptions) || classOptions.length === 0) return;
        if (!Array.isArray(legalStatusOptions) || legalStatusOptions.length === 0) return;
        if (!Array.isArray(loadStatusOptions) || loadStatusOptions.length === 0) return;

        const params = parseSearchParams(location.search);
        if (params.classFilter || params.legalStatusId || params.loadStatus) {
            const filterData = buildFilterFromParams(
                params,
                classOptions,
                legalStatusOptions,
                loadStatusOptions,
            );
            const mergedFilterData = { ...filterForm, ...filterData };
            updateProductsSearchForm({ key: 'filterForm', data: filterData });

            // Handle pageSize if present
            let rangeOverride;
            if (params.pageSize) {
                const newPageSize = parseInt(params.pageSize, 10);
                if (!isNaN(newPageSize) && newPageSize > 0) {
                    rangeOverride = getItemRange(0, newPageSize);
                }
            }

            fetchData(mergedFilterData, rangeOverride);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [classOptions, legalStatusOptions, loadStatusOptions]);

    /**
     * Builds filter form data from URL search params.
     * Converts string params to appropriate types and matches select options.
     */
    function buildFilterFromParams(params, classOpts, legalStatusOpts, loadStatusOpts) {
        const filterData = {};

        if (params.productId) filterData.productId = params.productId;
        if (params.productTitle) filterData.productTitle = params.productTitle;
        if (params.startYear) filterData.startYear = params.startYear;
        if (params.endYear) filterData.endYear = params.endYear;
        if (params.foxFinancialTitleId) filterData.foxFinancialTitleId = params.foxFinancialTitleId;

        // Match select options (only if options are loaded)
        if (params.classFilter && Array.isArray(classOpts) && classOpts.length > 0) {
            const matched = findClassByValue(classOpts, params.classFilter);
            if (matched) filterData.classFilter = matched;
        }

        if (params.legalStatusId && Array.isArray(legalStatusOpts) && legalStatusOpts.length > 0) {
            const matched = findLegalStatusByValue(legalStatusOpts, params.legalStatusId);
            if (matched) filterData.legalStatusId = matched;
        }

        if (params.loadStatus && Array.isArray(loadStatusOpts) && loadStatusOpts.length > 0) {
            const matched = findLoadStatusValue(loadStatusOpts, params.loadStatus);
            if (matched) filterData.loadStatus = matched;
        }

        return filterData;
    }

    /**
     * Fetches products data and updates Redux state.
     * @param {Object} filterData - Filter criteria
     * @param {Array} rangeOverride - Optional range override (e.g., when pageSize changes)
     */
    const fetchData = async (filterData, rangeOverride) => {
        // Merge with current filterForm to include all fields
        const mergedFilter = { ...filterForm, ...filterData };
        const range = rangeOverride ?? getItemRange(0, itemsPerPage);
        const data = await fetchProducts(range, mergedFilter);
        if (data) {
            updateProducts({
                products: data?.content ?? [],
                totalPages: data?.totalPages ?? 0,
                currentPage: data?.pageable?.pageNumber ?? 0,
                totalItems: data?.totalElements ?? 0,
                productRange: range,
            });
        }
    };

    /**
     * Reset navigates to clean URL; the location.key useEffect handles re-fetch.
     */
    const handleResetFilter = () => {
        clearProducts();
        setValidationErrors({ productId: undefined, foxFinancialTitleId: undefined });
        navigate(location.pathname);
    };

    /**
     * Handles input changes with validation and debounced search.
     * Validates ID fields for special characters.
     * Triggers search after 400ms of no typing.
     */
    function handleInputChange(field, value) {
        let newValidationErrors = validationErrors;

        // Validate ID fields for special characters
        if (field === 'productId' || field === 'foxFinancialTitleId') {
            const error = validateSpecialCharacters(value);
            newValidationErrors = { ...validationErrors, [field]: error };
            setValidationErrors(newValidationErrors);
        }

        // Update form state
        const newFilterForm = { ...filterForm, [field]: value };
        updateProductsSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(newValidationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateProducts({ productRange: firstPageRange, currentPage: 0 });
                const filtered = cleanProductFilter(newFilterForm, firstPageRange, productSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    /**
     * Handles select changes with debounced search.
     */
    function handleSelectChange(field, value) {
        const newFilterForm = { ...filterForm, [field]: value };
        updateProductsSearchForm({
            key: 'filterForm',
            data: { [field]: value },
        });

        // Debounce the search to avoid excessive API calls
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
        }
        debounceTimerRef.current = setTimeout(() => {
            const hasErrors = Object.values(validationErrors).some((error) => error);
            if (!hasErrors) {
                const firstPageRange = getItemRange(0, itemsPerPage);
                updateProducts({ productRange: firstPageRange, currentPage: 0 });
                const filtered = cleanProductFilter(newFilterForm, firstPageRange, productSort);
                updateSearchParams(location, navigate, filtered);
            }
        }, 400);
    }

    return (
        <Row className="products-filter-form">
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Product ID">
                    <Input
                        value={filterForm.productId}
                        onChange={(e) => handleInputChange('productId', e.target.value)}
                        width="auto"
                        id="productId"
                        name="productId"
                        placeholder="Enter"
                        className={validationErrors.productId ? 'input-error' : ''}
                    />
                    {validationErrors.productId && (
                        <div className="validation-error">{validationErrors.productId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Title">
                    <Input
                        value={filterForm.productTitle}
                        onChange={(e) => handleInputChange('productTitle', e.target.value)}
                        width="auto"
                        id="productTitle"
                        name="productTitle"
                        placeholder="Enter"
                    />
                </InputLabel>
            </Col>
            {!isLegalUser && (
                <Col>
                    <InputLabel colProps={{ span: 24 }} label="Start Year">
                        <Input
                            value={filterForm.startYear}
                            onChange={(e) => handleInputChange('startYear', e.target.value)}
                            width="auto"
                            id="startYear"
                            name="startYear"
                            placeholder="Enter"
                        />
                    </InputLabel>
                </Col>
            )}
            {!isLegalUser && (
                <Col>
                    <InputLabel colProps={{ span: 24 }} label="End Year">
                        <Input
                            value={filterForm.endYear}
                            onChange={(e) => handleInputChange('endYear', e.target.value)}
                            width="auto"
                            id="endYear"
                            name="endYear"
                            placeholder="Enter"
                        />
                    </InputLabel>
                </Col>
            )}
            <Col>
                <InputLabel colProps={{ span: 24 }} label="FOX WPR ID">
                    <Input
                        value={filterForm.foxFinancialTitleId}
                        onChange={(e) => handleInputChange('foxFinancialTitleId', e.target.value)}
                        width="auto"
                        id="foxFinancialTitleId"
                        name="foxFinancialTitleId"
                        placeholder="Enter"
                        className={validationErrors.foxFinancialTitleId ? 'input-error' : ''}
                    />
                    {validationErrors.foxFinancialTitleId && (
                        <div className="validation-error">{validationErrors.foxFinancialTitleId}</div>
                    )}
                </InputLabel>
            </Col>
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Class">
                    <Select
                        options={classOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleSelectChange('classFilter', value)}
                        value={filterForm.classFilter}
                    />
                </InputLabel>
            </Col>
            {!isLegalUser && (
                <Col>
                    <InputLabel colProps={{ span: 24 }} label="Load Status">
                        <Select
                            options={loadStatusOptions}
                            isClearable
                            isSearchable
                            onChange={(value) => handleSelectChange('loadStatus', value)}
                            value={filterForm.loadStatus}
                        />
                    </InputLabel>
                </Col>
            )}
            <Col>
                <InputLabel colProps={{ span: 24 }} label="Legal Status">
                    <Select
                        options={filteredLegalStatusOptions}
                        isClearable
                        isSearchable
                        onChange={(value) => handleSelectChange('legalStatusId', value)}
                        value={filterForm.legalStatusId}
                    />
                </InputLabel>
            </Col>
            <Col>
                <Button
                    className="reset-button"
                    sizeType="small"
                    styleType="standard"
                    onClick={handleResetFilter}
                >
                    Reset
                </Button>
            </Col>
        </Row>
    );
}
